import React, { useCallback, useEffect, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  Divider,
  Paper,
  Stack,
} from "@mui/material";
import { Add, Delete, Close } from "@mui/icons-material";

import CustomTextField from "../../../components/common/Textfield";
import apiService from "../../../service/Api";
import useDebounce from "../../../hooks/useDebounce";
import { ChecklistRow } from "../Checklists";
import { toast } from "react-toastify";

export interface RoleItem {
  id: number;
  name: string;
}

interface ListItemDraft {
  id?: number;
  name: string;
  points: number;
}

interface SectionDraft {
  id?: number;
  name: string;
  list_items: ListItemDraft[];
}

const PHASE_OPTIONS = [
  { value: "pre_stream", label: "Pre-Stream" },
  { value: "on_stream", label: "On-Stream" },
  { value: "post_stream", label: "Post-Stream" },
] as const;

type PhaseValue = (typeof PHASE_OPTIONS)[number]["value"];

interface SearchRoleDialogProps {
  searchRoleOpen: boolean;
  setSearchRoleOpen: (open: boolean) => void;
  searchResults: RoleItem | null;
  setSearchResults: (role: RoleItem | null) => void;
  refreshChecklists: () => void;
  mode?: "add" | "edit";
  editChecklist?: ChecklistRow | null;
}

const emptySection = (): SectionDraft => ({ name: "", list_items: [] });

export const SearchRoleDialog: React.FC<SearchRoleDialogProps> = ({
  searchRoleOpen,
  setSearchRoleOpen,
  searchResults,
  setSearchResults,
  refreshChecklists,
  mode,
  editChecklist,
}) => {
  const [roleOptions, setRoleOptions] = useState<RoleItem[]>([]);
  const [roleVal, setRoleVal] = useState("");
  const [rolesLoading, setRolesLoading] = useState(false);
  const debouncedRoleSearch = useDebounce(roleVal, 400);

  const [checklistName, setChecklistName] = useState("");
  const [description, setDescription] = useState("");
  const [phase, setPhase] = useState<PhaseValue | null>(null);
  const [sections, setSections] = useState<SectionDraft[]>([]);
  // Text currently typed into each section's "add task" row, keyed by section index
  const [draftItemText, setDraftItemText] = useState<Record<number, string>>(
    {},
  );
  const [saving, setSaving] = useState(false);

  const isEdit = mode === "edit" && !!editChecklist;

  useEffect(() => {
    if (!searchRoleOpen) return;

    setRoleOptions([]);
    setDraftItemText({});

    if (isEdit && editChecklist) {
      setChecklistName(editChecklist.name);
      setDescription(editChecklist.description ?? "");
      setPhase(editChecklist.phase);
      setRoleVal(editChecklist.roleName ?? "");
      setSearchResults(
        editChecklist.roleId
          ? { id: editChecklist.roleId, name: editChecklist.roleName }
          : null,
      );
      setSections(
        editChecklist.sections.map((s) => ({
          id: s.id,
          name: s.name,
          list_items: (s.list_items ?? []).map((i) => ({
            id: i.id,
            name: i.name,
            points: i.points ?? 0,
          })),
        })),
      );
    } else {
      setRoleVal("");
      setChecklistName("");
      setDescription("");
      setPhase(null);
      setSearchResults(null);
      setSections([emptySection()]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchRoleOpen, isEdit]);

  const fetchRoles = useCallback(async (searching: string) => {
    if (!searching) {
      setRoleOptions([]);
      return;
    }
    setRolesLoading(true);
    try {
      const res = await apiService.get<{ results: RoleItem[] }>(
        "/api/v1/crews/roles/",
        { search: searching, page_size: 10 },
      );
      setRoleOptions(res.data.results || []);
    } catch (err) {
      console.error("Failed to fetch roles:", err);
      setRoleOptions([]);
    } finally {
      setRolesLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRoles(debouncedRoleSearch);
  }, [debouncedRoleSearch, fetchRoles]);

  // ---- Section management ----
  const addSection = () => setSections((prev) => [...prev, emptySection()]);

  const removeSection = (index: number) => {
    setSections((prev) => prev.filter((_, i) => i !== index));
    setDraftItemText((prev) => {
      const next = { ...prev };
      delete next[index];
      return next;
    });
  };

  const renameSection = (index: number, name: string) =>
    setSections((prev) =>
      prev.map((s, i) => (i === index ? { ...s, name } : s)),
    );

  // ---- Task item management — Enter-to-add pattern ----
  const commitDraftItem = (sectionIndex: number) => {
    const text = (draftItemText[sectionIndex] ?? "").trim();
    if (!text) return;

    setSections((prev) =>
      prev.map((s, i) =>
        i === sectionIndex
          ? { ...s, list_items: [...s.list_items, { name: text, points: 1 }] }
          : s,
      ),
    );
    setDraftItemText((prev) => ({ ...prev, [sectionIndex]: "" }));
  };

  const handleDraftKeyDown = (
    e: React.KeyboardEvent<HTMLDivElement>,
    sectionIndex: number,
  ) => {
    if (e.key === "Enter") {
      e.preventDefault();
      commitDraftItem(sectionIndex);
    }
  };
  const updateItem = (
    sectionIndex: number,
    itemIndex: number,
    field: "name" | "points",
    value: string | number,
  ) =>
    setSections((prev) =>
      prev.map((s, i) =>
        i === sectionIndex
          ? {
              ...s,
              list_items: s.list_items.map((item, j) =>
                j === itemIndex ? { ...item, [field]: value } : item,
              ),
            }
          : s,
      ),
    );

  const removeItem = (sectionIndex: number, itemIndex: number) =>
    setSections((prev) =>
      prev.map((s, i) =>
        i === sectionIndex
          ? { ...s, list_items: s.list_items.filter((_, j) => j !== itemIndex) }
          : s,
      ),
    );

  const closeAndReset = () => {
    setSearchRoleOpen(false);
    setSearchResults(null);
    setSections([]);
    setDraftItemText({});
  };

  const validate = (): string | null => {
    if (!checklistName.trim()) return "Give the checklist a name.";
    if (!searchResults) return "Assign a role.";
    if (!phase) return "Pick when this checklist applies.";
    if (sections.length === 0) return "Add at least one section.";
    for (const s of sections) {
      if (!s.name.trim()) return "Every section needs a name.";
      if (s.list_items.length === 0)
        return `"${s.name}" needs at least one task — type one and press enter.`;
    }
    return null;
  };

  const buildPayload = () => ({
    name: checklistName,
    description: description || null,
    roles: searchResults?.id,
    phase,
    notes: null,
    sections: sections.map((s, sectionIndex) => ({
      ...(s.id ? { id: s.id } : {}),
      name: s.name,
      description: null,
      order: sectionIndex,
      list_items: s.list_items.map((item, itemIndex) => ({
        ...(item.id ? { id: item.id } : {}),
        name: item.name,
        description: null,
        points: item.points,
        order: itemIndex,
      })),
    })),
  });

  const handleSave = async () => {
    const error = validate();
    if (error) {
      toast.error(error);
      return;
    }

    setSaving(true);
    try {
      if (isEdit && editChecklist) {
        await apiService.patch(
          `/api/checklists/checklist/${editChecklist.id}/`,
          buildPayload(),
        );
        toast.success(`"${checklistName}" updated successfully.`);
      } else {
        await apiService.post(
          "/api/checklists/checklist/full-create/",
          buildPayload(),
        );
        toast.success(`"${checklistName}" created successfully.`);
      }
      refreshChecklists();
      closeAndReset();
    } catch (err) {
      console.error("Failed to save checklist:", err);
      toast.error(
        "Failed to save checklist — please check the fields and try again.",
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={searchRoleOpen}
      onClose={closeAndReset}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        {isEdit ? "Edit Checklist" : "Add Checklist"}
        <IconButton onClick={closeAndReset}>
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={2.5}>
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
              Checklist Name
            </Typography>
            <CustomTextField
              placeholder="e.g. Camera"
              value={checklistName}
              onChange={(e) => setChecklistName(e.target.value)}
            />
          </Box>

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
              Description
            </Typography>
            <CustomTextField
              placeholder="Add context for the crew member"
              multiline
              rows={2}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </Box>

          <Box sx={{ display: "flex", gap: 2 }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                Assign To Role
              </Typography>
              <Autocomplete
                options={roleOptions}
                getOptionLabel={(option) => option.name}
                value={searchResults}
                onChange={(_, newValue) => setSearchResults(newValue)}
                inputValue={roleVal}
                onInputChange={(_, newInput) => setRoleVal(newInput)}
                loading={rolesLoading}
                renderInput={(params) => (
                  <CustomTextField
                    {...params}
                    placeholder="Search roles..."
                    helperText="Type to search"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {rolesLoading ? <CircularProgress size={18} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </Box>

            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                Applies When
              </Typography>
              <Autocomplete
                options={PHASE_OPTIONS}
                getOptionLabel={(o) => o.label}
                value={PHASE_OPTIONS.find((p) => p.value === phase) ?? null}
                onChange={(_, v) => setPhase(v?.value ?? null)}
                renderInput={(params) => (
                  <CustomTextField {...params} placeholder="Select phase" />
                )}
              />
            </Box>
          </Box>

          <Divider />

          {/* ---- Sections builder ---- */}
          <Box>
            <Typography variant="h6" sx={{ mb: 0.25 }}>
              Sections
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mb: 1.5 }}
            >
              Sections group related tasks — e.g. Pre-setup, During stream, Wrap
              up.
            </Typography>

            <Stack spacing={1.5}>
              {sections.map((section, sectionIndex) => (
                <Paper
                  key={sectionIndex}
                  variant="outlined"
                  sx={{ p: 2, borderRadius: 2 }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      gap: 1,
                      alignItems: "center",
                      mb: 1.5,
                    }}
                  >
                    <Box
                      sx={{
                        width: 22,
                        height: 22,
                        borderRadius: "50%",
                        bgcolor: "primary.light",
                        color: "primary.dark",
                        fontSize: 12,
                        fontWeight: 600,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      {sectionIndex + 1}
                    </Box>
                    <CustomTextField
                      placeholder="Section name, e.g. Pre-Setup"
                      value={section.name}
                      onChange={(e) =>
                        renameSection(sectionIndex, e.target.value)
                      }
                      sx={{
                        flex: 1,
                        "& .MuiInputBase-input": { fontWeight: 500 },
                      }}
                    />
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ whiteSpace: "nowrap" }}
                    >
                      {section.list_items.length}{" "}
                      {section.list_items.length === 1 ? "task" : "tasks"}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => removeSection(sectionIndex)}
                      disabled={sections.length === 1}
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  </Box>

                  <Box sx={{ pl: "30px" }}>
                    <Stack spacing={0.75} sx={{ mb: 1 }}>
                      {section.list_items.map((item, itemIndex) => (
                        <Box
                          key={itemIndex}
                          sx={{ display: "flex", gap: 1, alignItems: "center" }}
                        >
                          <CustomTextField
                            value={item.name}
                            onChange={(e) =>
                              updateItem(
                                sectionIndex,
                                itemIndex,
                                "name",
                                e.target.value,
                              )
                            }
                            sx={{ flex: 1 }}
                          />
                          <CustomTextField
                            type="number"
                            value={item.points}
                            onChange={(e) =>
                              updateItem(
                                sectionIndex,
                                itemIndex,
                                "points",
                                parseInt(e.target.value) || 0,
                              )
                            }
                            sx={{ width: 70 }}
                          />
                          <IconButton
                            size="small"
                            onClick={() => removeItem(sectionIndex, itemIndex)}
                          >
                            <Close fontSize="small" />
                          </IconButton>
                        </Box>
                      ))}
                    </Stack>

                    <CustomTextField
                      placeholder="Add a task…"
                      helperText="Press Enter or click + to add"
                      value={draftItemText[sectionIndex] ?? ""}
                      onChange={(e) =>
                        setDraftItemText((prev) => ({
                          ...prev,
                          [sectionIndex]: e.target.value,
                        }))
                      }
                      onKeyDown={(e) => handleDraftKeyDown(e, sectionIndex)}
                      onBlur={() => commitDraftItem(sectionIndex)}
                      InputProps={{
                        endAdornment: (
                          <IconButton
                            size="small"
                            onClick={() => commitDraftItem(sectionIndex)}
                            disabled={
                              !(draftItemText[sectionIndex] ?? "").trim()
                            }
                            edge="end"
                          >
                            <Add fontSize="small" />
                          </IconButton>
                        ),
                      }}
                    />
                  </Box>
                </Paper>
              ))}
            </Stack>

            {/* New section — visually distinct drop-zone style, separate from "add task" */}
            <Button
              onClick={addSection}
              variant="outlined"
              startIcon={<Add />}
              fullWidth
              sx={{
                mt: 1.5,
                py: 1.25,
                borderWidth: 1.5,
                borderStyle: "solid",
                borderRadius: 2,
                fontWeight: 600,
                textTransform: "none",
                "&:hover": { borderWidth: 1.5 },
              }}
            >
              Add Section
            </Button>
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={closeAndReset} disabled={saving}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? (
            <CircularProgress size={20} />
          ) : isEdit ? (
            "Save Changes"
          ) : (
            "Add Checklist"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SearchRoleDialog;
